import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  ShieldAlert, AlertTriangle, Ban, CheckCircle, XCircle, Trash2, Loader2, RefreshCw,
  Eye, Building2, User, Flag,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// -------------------------------------------------------------
// Small helpers
// -------------------------------------------------------------
const VerdictPill = ({ verdict, score }) => {
  const config = {
    likely_illegal: { className: 'bg-red-100 text-red-800 border-red-300', Icon: Ban, label: 'Likely illegal' },
    suspicious:     { className: 'bg-amber-100 text-amber-800 border-amber-300', Icon: AlertTriangle, label: 'Suspicious' },
    clean:          { className: 'bg-emerald-100 text-emerald-800 border-emerald-300', Icon: CheckCircle, label: 'Clean' },
  }[verdict] || { className: 'bg-gray-100 text-gray-700 border-gray-300', Icon: Flag, label: verdict || 'unknown' };
  const { Icon } = config;
  return (
    <span
      data-testid={`verdict-pill-${verdict}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${config.className}`}
    >
      <Icon className="h-3 w-3" /> {config.label} · {score ?? 0}
    </span>
  );
};

const HitBadge = ({ hit }) => (
  <span
    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono bg-slate-50 text-slate-700 border-slate-300"
    title={`Layer ${hit.layer} · weight ${hit.weight}`}
  >
    <Flag className="h-2.5 w-2.5" />
    {hit.category}
    <span className="text-slate-400">"{hit.match?.slice(0, 24)}"</span>
  </span>
);

// -------------------------------------------------------------
// Row
// -------------------------------------------------------------
const ListingRow = ({ listing, onDecide }) => {
  const tm = listing.trust_moderation || {};
  const seller = listing.seller || {};
  const [rejectOpen, setRejectOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const decide = async (decision, note) => {
    setSaving(true);
    try {
      await onDecide(listing.id, decision, note);
      setRejectOpen(false);
      setDeleteOpen(false);
    } finally { setSaving(false); }
  };

  return (
    <Card
      className="border-l-4 border-l-amber-400 hover:shadow-md transition-shadow"
      data-testid={`shadow-row-${listing.id}`}
    >
      <CardContent className="p-4 flex gap-4">
        {listing.images?.[0] && (
          <img
            src={listing.images[0].startsWith('http') ? listing.images[0] : `${API}${listing.images[0]}`}
            alt={listing.title}
            className="w-24 h-24 object-cover rounded border shrink-0"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="min-w-0">
              <h3
                className="font-semibold text-slate-900 truncate"
                data-testid={`shadow-title-${listing.id}`}
              >
                {listing.title}
              </h3>
              <div className="flex items-center gap-2 text-xs text-slate-600 mt-0.5">
                <span>{listing.category}</span>
                <span>·</span>
                <span>{listing.location} · {listing.country}</span>
                <span>·</span>
                <span className="font-semibold text-slate-900">
                  {listing.price != null ? `${listing.price}` : '-'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <VerdictPill verdict={tm.verdict} score={tm.score} />
              {tm.action === 'held_for_review' && (
                <Badge className="bg-red-600 text-white">HELD</Badge>
              )}
              {tm.action === 'shadow_queue' && (
                <Badge className="bg-amber-500 text-white">SHADOW</Badge>
              )}
            </div>
          </div>

          <p className="text-sm text-slate-700 line-clamp-2 mb-2">
            {listing.description}
          </p>

          {/* AI hits */}
          {tm.hits?.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {tm.hits.slice(0, 6).map((h, i) => <HitBadge key={i} hit={h} />)}
              {tm.hits.length > 6 && (
                <span className="text-[10px] text-slate-500 self-center">
                  +{tm.hits.length - 6} more
                </span>
              )}
            </div>
          )}

          {/* Seller strip */}
          <div className="flex items-center gap-3 text-xs text-slate-600 mb-3 pb-3 border-b">
            {seller.seller_type === 'business' ? (
              <Building2 className="h-3.5 w-3.5 text-blue-600" />
            ) : (
              <User className="h-3.5 w-3.5" />
            )}
            <span className="font-medium">{seller.business_name || seller.name || 'Unknown'}</span>
            <span className="text-slate-400">·</span>
            <span>{seller.email}</span>
            <span className="text-slate-400">·</span>
            <Badge
              variant="outline"
              className={
                seller.trust_tier === 'GOLD' ? 'text-yellow-700 border-yellow-400'
                : seller.trust_tier === 'VERIFIED' ? 'text-blue-700 border-blue-400'
                : seller.trust_tier === 'BLOCKED' ? 'text-red-700 border-red-400'
                : 'text-slate-600 border-slate-400'
              }
            >
              {seller.trust_tier || 'UNVERIFIED'} · {seller.trust_score ?? 0}
            </Badge>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(`/listing/${listing.id}`, '_blank')}
              data-testid={`shadow-view-${listing.id}`}
            >
              <Eye className="h-3.5 w-3.5 mr-1" /> View
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => decide('approve')}
              disabled={saving}
              data-testid={`shadow-approve-${listing.id}`}
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5 mr-1" />}
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-50"
              onClick={() => setRejectOpen(true)}
              disabled={saving}
              data-testid={`shadow-reject-${listing.id}`}
            >
              <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-red-500 text-red-700 hover:bg-red-50"
              onClick={() => setDeleteOpen(true)}
              disabled={saving}
              data-testid={`shadow-delete-${listing.id}`}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
            </Button>
          </div>
        </div>
      </CardContent>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject listing</DialogTitle>
            <DialogDescription>
              The listing will be hidden from the public feed. Optionally add a note (visible only to admins).
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid={`shadow-reject-reason-${listing.id}`}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => decide('reject', reason)}
              disabled={saving}
              data-testid={`shadow-reject-confirm-${listing.id}`}
            >
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Confirm reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete listing permanently?</DialogTitle>
            <DialogDescription>
              This will hard-delete the record. Prefer <b>Reject</b> for reversible action.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={() => decide('delete')}
              disabled={saving}
              data-testid={`shadow-delete-confirm-${listing.id}`}
            >
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Yes, delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// -------------------------------------------------------------
// Page
// -------------------------------------------------------------
export default function AdminListingQueue() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all'); // all|shadow_queue|held_for_review|reviewed
  const [loading, setLoading] = useState(true);

  const token = () => localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filter === 'shadow_queue' || filter === 'held_for_review') {
        params.action = filter;
      }
      if (filter === 'reviewed') {
        params.reviewed = true;
      }
      const [queueRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/trust/admin/listings/queue`, {
          headers: { Authorization: `Bearer ${token()}` },
          params,
        }),
        axios.get(`${API}/api/trust/admin/stats`, {
          headers: { Authorization: `Bearer ${token()}` },
        }),
      ]);
      setItems(queueRes.data.listings || []);
      setStats(statsRes.data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error('Please log in');
        navigate('/login');
      } else if (e.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load queue');
      }
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => { load(); }, [load]);

  const decide = async (listingId, decision, note) => {
    try {
      await axios.patch(
        `${API}/api/trust/admin/listings/${listingId}`,
        { decision, reason: note || null },
        { headers: { Authorization: `Bearer ${token()}` } },
      );
      toast.success(`Listing ${decision === 'delete' ? 'deleted' : `${decision}d`}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    }
  };

  const l = stats?.listings || {};

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-listing-queue">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="listing-queue-title">
            <ShieldAlert className="h-6 w-6 text-amber-600" />
            Listing Moderation Queue
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            AI-flagged listings from the trust classifier. Review, approve, or remove content
            that hit blocklist rules, category mismatches, or contact-evasion patterns.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="listing-queue-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Held (blocked)</div>
            <div className="text-2xl font-bold text-red-700" data-testid="stat-held-pending">
              {l.held_pending ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Shadow queue</div>
            <div className="text-2xl font-bold text-amber-700" data-testid="stat-shadow-pending">
              {l.shadow_pending ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Already reviewed</div>
            <div className="text-2xl font-bold text-emerald-700" data-testid="stat-reviewed">
              {l.reviewed ?? 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Total flagged</div>
            <div className="text-2xl font-bold text-slate-800">
              {(l.held_pending ?? 0) + (l.shadow_pending ?? 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'all', label: 'All flagged' },
          { key: 'held_for_review', label: 'Held (blocked)' },
          { key: 'shadow_queue', label: 'Shadow queue' },
          { key: 'reviewed', label: 'Reviewed' },
        ].map((t) => (
          <Button
            key={t.key}
            size="sm"
            variant={filter === t.key ? 'default' : 'outline'}
            onClick={() => setFilter(t.key)}
            data-testid={`filter-${t.key}`}
          >
            {t.label}
          </Button>
        ))}
      </div>

      {/* List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading queue…
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              <CheckCircle className="h-10 w-10 mx-auto mb-2 text-emerald-500" />
              <p className="font-semibold text-slate-800">Queue clear</p>
              <p className="text-sm">No listings match the current filter. Nice work!</p>
            </CardContent>
          </Card>
        ) : (
          items.map((listing) => (
            <ListingRow key={listing.id} listing={listing} onDecide={decide} />
          ))
        )}
      </div>
    </div>
  );
}
